import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import {
  getDateTime,
  sortByDate,
  type Exception,
  type ExceptionData,
  type ExceptionForm,
  type ExceptionTableFilters,
  deleteException,
} from '@frontend/api-schedule-exceptions';
import { useTranslation } from '@frontend/i18n';
import { useModalControl, ConfirmationModal, useAlert, Button } from '@frontend/design-system';
import { shouldInvalidateExceptions } from '../../utils';
import { ScheduleExceptionsModal } from '../ScheduleExceptionsModal';
import { ScheduleExceptionsTable } from '../ScheduleExceptionsTable';
import { primaryButtonStyles } from './styles';

interface Props {
  locationId: string;
  data?: Exception[];
  providerId?: string;
  filters: ExceptionTableFilters;
  handleFiltersUpdate: (newFilters: ExceptionTableFilters) => void;
  checkConflicts?: (newException: ExceptionForm) => boolean | undefined;
  isLoading: boolean;
}

export const ScheduleExceptions = ({
  locationId,
  data,
  providerId,
  filters,
  handleFiltersUpdate,
  checkConflicts,
  isLoading,
}: Props) => {
  const { t } = useTranslation('schedule');
  const queryClient = useQueryClient();
  const alert = useAlert();

  const [selectedExceptionId, setSelectedExceptionId] = useState<string | undefined>();

  const upcomingExceptions = useMemo(() => {
    return sortByDate(data);
  }, [data]);

  const { modalProps: editModalProps, triggerProps: editTriggerProps } = useModalControl();

  const { modalProps: deleteModalProps, triggerProps: deleteTriggerProps } = useModalControl();

  const handleAddNew = () => {
    setSelectedExceptionId(undefined);
    editTriggerProps.onClick();
  };

  const exceptionsDataToDisplay = useMemo((): ExceptionData[] => {
    return (
      upcomingExceptions?.map((exception: Exception) => {
        const [startDate, endDate, startTime, endTime] = getDateTime(exception?.startDateTime, exception?.endDateTime);
        return {
          id: exception?.id || '',
          name: exception?.name,
          isAvailable: exception?.isAvailable ? 'true' : 'false',
          startDate,
          endDate,
          startTime,
          endTime,
        };
      }) || []
    );
  }, [upcomingExceptions]);

  const selectedException = useMemo(() => {
    if (!selectedExceptionId || !exceptionsDataToDisplay) {
      return {};
    }
    return exceptionsDataToDisplay?.find(({ id }) => id === selectedExceptionId);
  }, [selectedExceptionId, exceptionsDataToDisplay]);

  const handleEdit = useCallback(
    (exceptionId?: string, e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e?.stopPropagation();
      setSelectedExceptionId(exceptionId);
      editTriggerProps.onClick();
    },
    [editTriggerProps]
  );

  const handleDelete = useCallback(
    (exceptionId?: string, e?: React.MouseEvent<HTMLElement, MouseEvent>) => {
      e?.stopPropagation();
      setSelectedExceptionId(exceptionId);
      deleteTriggerProps.onClick();
    },
    [deleteTriggerProps]
  );

  const handleConfirmDelete = async () => {
    if (!selectedExceptionId) {
      return;
    }
    try {
      await deleteException(locationId, selectedExceptionId, providerId);
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => shouldInvalidateExceptions(queryKey, locationId, providerId),
      });
      alert.success(t('Successfully deleted exception'));
    } catch (error: any) {
      console.error(error);
      alert.error(t('Failed to delete exception'));
    } finally {
      setSelectedExceptionId(undefined);
    }
  };

  return (
    <>
      <Button
        variant='primary'
        size='large'
        css={primaryButtonStyles}
        {...editTriggerProps}
        onClick={handleAddNew}
        iconName='plus-small'
      >
        {t('Add Exception')}
      </Button>

      <ScheduleExceptionsTable
        exceptionData={exceptionsDataToDisplay}
        handleDelete={handleDelete}
        handleEdit={handleEdit}
        filters={filters}
        handleFiltersUpdate={handleFiltersUpdate}
        isLoading={isLoading}
      />

      <ScheduleExceptionsModal
        {...editModalProps}
        data={selectedException as ExceptionData}
        locationId={locationId}
        providerId={providerId}
        checkConflicts={checkConflicts}
      />

      <ConfirmationModal
        {...deleteModalProps}
        title={t('Delete Exception')}
        destructive
        message={t('Are you sure you want to delete this exception?')}
        confirmLabel={t('Delete')}
        cancelLabel={t('Cancel')}
        onCancel={deleteModalProps.onClose}
        onConfirm={handleConfirmDelete}
      />
    </>
  );
};
